import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Key, Flame, Drop, PencilSimple, CheckCircle, X, Warning } from "@phosphor-icons/react";

/**
 * PropertyDetailsCard — AppFolio-parity compliance + maintenance card.
 * Two rendering modes:
 *   • compact=true  → sidebar variant for WO detail (read-only, no edit btn)
 *   • compact=false → full card for the Property page (with edit modal)
 */
export default function PropertyDetailsCard({ address, compact = false }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => {
    if (!address) return;
    setLoading(true);
    api.get(`/properties/${encodeURIComponent(address)}/details`)
      .then(r => setD(r.data.details))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [address]);

  if (!address || loading) return null;

  const dollars = Math.round((d?.maintenance_limit_cents || 0) / 100);
  const isEmpty = !dollars && !d?.unit_entry_preauthorized && !d?.has_home_warranty
    && !d?.maintenance_notes && !d?.rental_license_number && !d?.water_access_code
    && !d?.fire_extinguisher_expiry && !d?.fire_panel_expiry && !d?.fire_sprinkler_expiry
    && !d?.rental_license_expiration && !d?.refuse_fee_note;

  if (compact) {
    // Sidebar variant on WO detail — hide entirely if nothing set
    if (isEmpty) return null;
    return (
      <div className="mt-4 border-2 border-slate-300 bg-white shadow-[3px_3px_0_#0f172a] rounded-sm"
        data-testid="wo-property-context-card">
        <div className="px-3 py-2 bg-slate-100 border-b-2 border-slate-200 flex items-center gap-2">
          <ShieldCheck weight="fill" size={14} className="text-indigo-600"/>
          <div className="font-display font-black uppercase tracking-wider text-[11px] text-slate-800">
            Property Context
          </div>
        </div>
        <div className="p-3 space-y-2 text-[11px]">
          {dollars > 0 && (
            <ContextRow label="Maintenance Limit" value={`$${dollars.toLocaleString()}`}
              highlight="emerald" testid="ctx-maint-limit"/>
          )}
          <ContextRow label="Entry Pre-authorized"
            value={d.unit_entry_preauthorized ? "YES · lockbox OK" : "no"}
            highlight={d.unit_entry_preauthorized ? "emerald" : "slate"}
            testid="ctx-entry-preauth"/>
          {d.has_home_warranty && <ContextRow label="Home Warranty" value="YES" highlight="cyan"/>}
          {d.rental_license_number && (
            <ContextRow label="Rental License"
              value={`#${d.rental_license_number}${d.rental_license_expiration ? ` · exp ${d.rental_license_expiration}` : ""}`}/>
          )}
          {d.water_access_code && <ContextRow label="Water Access Code" value={d.water_access_code}/>}
          {(d.fire_extinguisher_expiry || d.fire_panel_expiry || d.fire_sprinkler_expiry) && (
            <div className="pt-1 border-t border-slate-100 space-y-0.5">
              {d.fire_extinguisher_expiry && <ContextRow label="Fire Ext. Expiry" value={d.fire_extinguisher_expiry}/>}
              {d.fire_panel_expiry && <ContextRow label="Fire Panel Expiry" value={d.fire_panel_expiry}/>}
              {d.fire_sprinkler_expiry && <ContextRow label="Fire Sprinkler Expiry" value={d.fire_sprinkler_expiry}/>}
            </div>
          )}
          {d.maintenance_notes && (
            <div className="pt-1 border-t border-slate-100">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Maintenance Notes</div>
              <div className="text-[11px] text-slate-700 whitespace-pre-wrap mt-0.5">{d.maintenance_notes}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full card for Property page
  return (
    <>
      <div className="rugged-card p-4 border-l-4 border-indigo-500" data-testid="property-details-card">
        <div className="flex items-center gap-2">
          <ShieldCheck weight="fill" size={18} className="text-indigo-600"/>
          <h3 className="font-display font-black text-lg uppercase">Property Details</h3>
          <button onClick={() => setEditOpen(true)} data-testid="property-details-edit-btn"
            className="ml-auto h-8 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 text-slate-900 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            <PencilSimple weight="bold" size={11}/> Edit
          </button>
        </div>

        {isEmpty ? (
          <div className="mt-3 p-4 border-2 border-dashed border-slate-300 rounded text-center text-sm text-slate-500">
            No details set yet. Tap <b>Edit</b> to add maintenance limit, rental license, entry pre-auth, and more.
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Field label="Maintenance Limit" value={dollars ? `$${dollars.toLocaleString()}` : ""}
              hint="per-property owner-approval override" icon={<Key weight="bold" size={12}/>}/>
            <Field label="Unit Entry Pre-authorized" value={d.unit_entry_preauthorized ? "Yes" : "No"}/>
            <Field label="Home Warranty" value={d.has_home_warranty ? "Yes" : "No"}/>
            <Field label="Rental License #" value={d.rental_license_number}/>
            <Field label="Rental License Expiration" value={d.rental_license_expiration}/>
            <Field label="Rental License Note" value={d.rental_license_note}/>
            <Field label="Water Access Code" value={d.water_access_code} icon={<Drop weight="bold" size={12}/>}/>
            <Field label="Refuse Fee Note" value={d.refuse_fee_note}/>
            <Field label="Fire Extinguisher Insp. Expiry" value={d.fire_extinguisher_expiry} icon={<Flame weight="bold" size={12}/>}/>
            <Field label="Fire Panel Insp. Expiry" value={d.fire_panel_expiry} icon={<Flame weight="bold" size={12}/>}/>
            <Field label="Fire Sprinkler Insp. Expiry" value={d.fire_sprinkler_expiry} icon={<Flame weight="bold" size={12}/>}/>
            {d.maintenance_notes && (
              <div className="sm:col-span-2 pt-2 border-t border-slate-100">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Maintenance Notes</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{d.maintenance_notes}</div>
              </div>
            )}
          </div>
        )}
        {d.updated_at && (
          <div className="mt-3 text-[10px] text-slate-400">
            Updated {(d.updated_at || "").slice(0, 10)} by {d.updated_by || "—"}
          </div>
        )}
      </div>

      {editOpen && (
        <EditPropertyDetailsModal address={address} initial={d}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}/>
      )}
    </>
  );
}

function ContextRow({ label, value, highlight, testid }) {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-500",
    cyan: "bg-cyan-50 border-cyan-500",
    slate: "bg-slate-50 border-slate-300",
  }[highlight] || "bg-white border-slate-200";
  return (
    <div className={`px-2 py-1 border-l-4 ${cls}`} data-testid={testid}>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-[12px] font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Field({ label, value, hint, icon }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="font-bold text-slate-900">{value || <span className="text-slate-400 font-normal">—</span>}</div>
      {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
}

function EditPropertyDetailsModal({ address, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    maintenance_limit_cents: initial?.maintenance_limit_cents || 0,
    unit_entry_preauthorized: !!initial?.unit_entry_preauthorized,
    has_home_warranty: !!initial?.has_home_warranty,
    maintenance_notes: initial?.maintenance_notes || "",
    rental_license_number: initial?.rental_license_number || "",
    rental_license_expiration: initial?.rental_license_expiration || "",
    rental_license_note: initial?.rental_license_note || "",
    water_access_code: initial?.water_access_code || "",
    refuse_fee_note: initial?.refuse_fee_note || "",
    fire_extinguisher_expiry: initial?.fire_extinguisher_expiry || "",
    fire_panel_expiry: initial?.fire_panel_expiry || "",
    fire_sprinkler_expiry: initial?.fire_sprinkler_expiry || "",
  });
  const [busy, setBusy] = useState(false);
  const dollars = Math.round((f.maintenance_limit_cents || 0) / 100);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/properties/${encodeURIComponent(address)}/details`, f);
      toast.success("Property details saved");
      onSaved?.();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const txt = (key, placeholder = "") => ({
    value: f[key],
    onChange: (e) => setF({ ...f, [key]: e.target.value }),
    placeholder,
    className: "w-full h-9 px-2 border-2 border-slate-300 rounded text-sm font-medium focus:border-slate-500 focus:outline-none",
    "data-testid": `pd-input-${key.replace(/_/g, "-")}`,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose} data-testid="property-details-modal">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-200 bg-indigo-600 text-white flex items-center gap-2">
          <ShieldCheck weight="fill" size={20}/>
          <div className="flex-1">
            <div className="font-display font-black text-lg uppercase leading-none">Edit Property Details</div>
            <div className="text-[10px] font-bold tracking-widest uppercase opacity-80 mt-0.5">{address}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded"><X weight="bold" size={18}/></button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Maintenance */}
          <section>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Maintenance</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Maintenance Limit ($)</label>
                <div className="mt-1 flex items-center gap-1">
                  <span className="text-slate-500 font-black">$</span>
                  <input type="number" min="0" step="50" value={dollars}
                    onChange={(e) => setF({ ...f, maintenance_limit_cents: Math.max(0, parseInt(e.target.value || "0", 10)) * 100 })}
                    data-testid="pd-input-limit"
                    className="w-full h-9 px-2 border-2 border-slate-300 rounded font-mono font-black focus:border-slate-500 focus:outline-none"/>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">Overrides tenant-wide owner-approval threshold.</div>
              </div>
              <div className="flex flex-col gap-2 justify-end pb-1">
                <label className="inline-flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={f.unit_entry_preauthorized}
                    onChange={(e) => setF({ ...f, unit_entry_preauthorized: e.target.checked })}
                    data-testid="pd-input-entry"
                    className="w-4 h-4 accent-emerald-600"/>
                  Unit Entry Pre-authorized
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={f.has_home_warranty}
                    onChange={(e) => setF({ ...f, has_home_warranty: e.target.checked })}
                    data-testid="pd-input-warranty"
                    className="w-4 h-4 accent-cyan-600"/>
                  Has Home Warranty Coverage
                </label>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Maintenance Notes</label>
              <textarea rows={4} {...txt("maintenance_notes",
                "Lockbox codes, tech tips, insurance policy numbers…")}
                className="w-full mt-1 p-2 border-2 border-slate-300 rounded text-sm font-medium focus:border-slate-500 focus:outline-none"/>
            </div>
          </section>

          {/* Rental license */}
          <section>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Rental License</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Number</label>
                <input {...txt("rental_license_number")}/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Expiration (YYYY-MM-DD)</label>
                <input {...txt("rental_license_expiration", "2027-07-22")}/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Note</label>
                <input {...txt("rental_license_note")}/>
              </div>
            </div>
          </section>

          {/* Fire compliance */}
          <section>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Fire Compliance</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Extinguisher Expiry</label>
                <input {...txt("fire_extinguisher_expiry")}/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Panel Expiry</label>
                <input {...txt("fire_panel_expiry")}/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sprinkler Expiry</label>
                <input {...txt("fire_sprinkler_expiry")}/>
              </div>
            </div>
          </section>

          {/* Utilities */}
          <section>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Utilities</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Water Access Code</label>
                <input {...txt("water_access_code")}/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Refuse Fee Note</label>
                <input {...txt("refuse_fee_note")}/>
              </div>
            </div>
          </section>
        </div>

        <div className="p-3 border-t-2 border-slate-200 flex gap-2 justify-end bg-slate-50">
          <button onClick={onClose}
            className="h-10 px-4 border-2 border-slate-300 bg-white text-slate-700 font-black uppercase text-xs tracking-widest rounded">
            Cancel
          </button>
          <button onClick={save} disabled={busy} data-testid="pd-save-btn"
            className="h-10 px-5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
            <CheckCircle weight="fill" size={13}/>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
